import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SERVER_URL } from "../api";
import { getQuizById } from "../api/quiz";
import { postAnswer } from "../api/answer";
import { getGameById } from "../api/game";
import { updateGroup } from "../api/group";
import { setGame, setGroup } from "../redux/actions";
import { EscapeRoomElement, getPlayablePages } from "../components/EscapeRoomRenderer";
import { ROUTES } from "../utils/constants";
import { updateStats, getTimer } from "../utils/helpers";
import { alertError } from "../utils/logger";

const CANVAS_WIDTH = 960;
const CANVAS_HEIGHT = 540;

export default function EscapeRoomPlay() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const game = useSelector((state) => state.game);
	const group = useSelector((state) => state.group);

	const escapeRoom = game?.escapeRoom;
	const pages = escapeRoom?.pages || [];
	const playablePages = getPlayablePages(pages);

	const [pageIndex, setPageIndex] = useState(() => group?.escapeRoomPageIndex ?? 0);
	const [answers, setAnswers] = useState(() => group?.escapeRoomAnswers || {});

	// Sincronizar una vez al montar si el grupo ya tiene progreso
	const syncedRef = useRef(false);
	useEffect(() => {
		if (syncedRef.current || !group) return;
		syncedRef.current = true;
		if (Number.isFinite(group.escapeRoomPageIndex)) setPageIndex(group.escapeRoomPageIndex);
		if (group.escapeRoomAnswers && Object.keys(group.escapeRoomAnswers).length > 0) setAnswers(group.escapeRoomAnswers);
	}, [group]);
	const [quizzes, setQuizzes] = useState({});
	const [scale, setScale] = useState(1);
	const [loading, setLoading] = useState(false);
	const containerRef = useRef(null);

	const currentPage = playablePages[pageIndex] || playablePages[0];
	const elements = currentPage?.elements || [];

	// Clamp page index
	useEffect(() => {
		if (playablePages.length > 0 && pageIndex >= playablePages.length) {
			setPageIndex(playablePages.length - 1);
		}
	}, [playablePages.length, pageIndex]);

	// Fetch quizzes for quiz elements on current page
	useEffect(() => {
		const quizIds = [...new Set((elements || []).filter((el) => el.type === "quiz").map((el) => el.content?.quizId).filter(Boolean))];
		quizIds.forEach((id) => {
			if (!quizzes[id]) {
				getQuizById(id)
					.then((res) => {
						if (res.status === 200) setQuizzes((prev) => ({ ...prev, [id]: res.data }));
					})
					.catch(() => {});
			}
		});
	}, [currentPage?.pageId, elements]);

	// Scale to fit
	const updateScale = useCallback(() => {
		if (!containerRef.current) return;
		const rect = containerRef.current.getBoundingClientRect();
		const sx = rect.width / CANVAS_WIDTH;
		const sy = rect.height / CANVAS_HEIGHT;
		setScale(Math.max(0.5, Math.min(sx, sy)));
	});

	useEffect(() => {
		updateScale();
		const ro = containerRef.current && new ResizeObserver(updateScale);
		if (ro && containerRef.current) ro.observe(containerRef.current);
		return () => ro?.disconnect();
	}, [currentPage?.pageId, updateScale]);

	const persistPageAndVisit = useCallback(
		async (newPageIndex) => {
			const g = { ...group, escapeRoomPageIndex: newPageIndex, lastVisited: ROUTES.ESCAPE_ROOM };
			dispatch(setGroup(g));
			await updateStats(g);
		},
		[group, dispatch]
	);

	const handleButtonAction = useCallback(
		(el) => {
			const action = el?.content?.action;
			if (action === "nextPage") {
				if (pageIndex < playablePages.length - 1) {
					const next = pageIndex + 1;
					setPageIndex(next);
					persistPageAndVisit(next);
				} else {
					// Fin del escape room -> ir a pantalla final o resultados
					const g = { ...group, lastVisited: ROUTES.RESULTS };
					dispatch(setGroup(g));
					updateStats(g);
					navigate(ROUTES.RESULTS);
				}
			} else if (action === "prevPage" && pageIndex > 0) {
				const prev = pageIndex - 1;
				setPageIndex(prev);
				persistPageAndVisit(prev);
			}
		},
		[pageIndex, playablePages.length, group, dispatch, persistPageAndVisit, navigate]
	);

	const handleQuizOptionSelect = useCallback(
		async (elementId, optionIndex, correct) => {
			const el = elements.find((e) => e.elementId === elementId);
			if (!el?.content?.quizId || answers[elementId]) return;

			setLoading(true);
			const quiz = quizzes[el.content.quizId];
			const pointsCorrect = quiz?.pointsCorrect ?? 10;
			const pointsIncorrect = quiz?.pointsIncorrect ?? -5;
			const hintPenaltyOnPage = (elements || [])
				.filter((e) => e.type === "hint")
				.reduce((sum, h) => sum + (answers[h.elementId]?.hintPenaltyUsed || 0), 0);
			const delta = (correct ? pointsCorrect : pointsIncorrect) - hintPenaltyOnPage;

			try {
				const res = await postAnswer({
					scenario: null,
					quiz: el.content.quizId,
					group: group?._id,
					score: delta,
					time: getTimer() || 0,
					lastVisited: ROUTES.ESCAPE_ROOM,
					correct,
				});

				if (res.status === 201) {
					const serverGroup = res.data?.group;
					const newAnswers = { ...answers, [elementId]: { selectedIndex: optionIndex, correct, hintPenaltyUsed: hintPenaltyOnPage } };
					const updatedGroup = {
						...serverGroup,
						escapeRoomPageIndex: pageIndex,
						escapeRoomAnswers: newAnswers,
					};
					dispatch(setGroup(updatedGroup));
					setAnswers(newAnswers);
					await updateStats(updatedGroup);
				}
			} catch (err) {
				const code = err?.response?.data?.code;
				if (err?.response?.status === 409 && code === "E_DUPLICATE_ANSWER") {
					// Ya respondido - solo actualizar estado local
					setAnswers((prev) => ({ ...prev, [elementId]: { selectedIndex: optionIndex, correct } }));
				} else {
					alertError({ error: err, customMessage: err?.response?.data?.message || "No se pudo guardar la respuesta." });
				}
			}
			setLoading(false);
		},
		[answers, elements, group, quizzes, pageIndex, dispatch]
	);

	const handleHintUsed = useCallback(
		async (elementId, penalty) => {
			if (penalty <= 0) return;
			const newAnswers = {
				...answers,
				[elementId]: {
					...answers[elementId],
					hintPenaltyUsed: (answers[elementId]?.hintPenaltyUsed || 0) + penalty,
				},
			};
			setAnswers(newAnswers);
			// Persistir escapeRoomAnswers; la penalización se descuenta al enviar la respuesta del quiz
			const g = {
				...group,
				escapeRoomPageIndex: pageIndex,
				escapeRoomAnswers: newAnswers,
				lastVisited: ROUTES.ESCAPE_ROOM,
			};
			dispatch(setGroup(g));
			await updateStats(g);
		},
		[answers, group, pageIndex, dispatch]
	);

	// Cargar juego con escape room si hace falta (p. ej. al reconectar)
	useEffect(() => {
		const gid = game?._id || group?.game;
		if (gid && !game?.escapeRoom) {
			getGameById(gid).then((res) => {
				if (res.status === 200 && res.data) dispatch(setGame(res.data));
			});
		}
	}, [game?._id, game?.escapeRoom, group?.game, dispatch]);

	// Redirigir si no hay escape room
	useEffect(() => {
		if (game && !game.escapeRoom && group?.lastVisited !== ROUTES.ESCAPE_ROOM) {
			navigate(ROUTES.START_GAME);
		}
	}, [game, group?.lastVisited, navigate]);

	if (!escapeRoom || playablePages.length === 0) {
		return (
			<div style={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#0f172a", color: "#fff" }}>
				<div>Este juego no tiene escape room configurado o no hay páginas disponibles.</div>
			</div>
		);
	}

	const bgColor = currentPage?.backgroundColor || "#fff";
	const bgImgUrl = currentPage?.backgroundImageUrl;
	const bgImgResolved = bgImgUrl && (bgImgUrl.startsWith("http") || bgImgUrl.startsWith("/") || bgImgUrl.startsWith("data:")) ? bgImgUrl : bgImgUrl ? `${SERVER_URL}/${bgImgUrl}` : null;
	const bgOpacity = currentPage?.backgroundImageOpacity != null ? currentPage.backgroundImageOpacity / 100 : 1;

	return (
		<div style={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column", background: "#0f172a", overflow: "hidden", zIndex: 1 }}>
			<div
				ref={containerRef}
				style={{
					position: "absolute",
					inset: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<div
					className="escape-room-canvas"
					style={{
						position: "relative",
						width: CANVAS_WIDTH,
						height: CANVAS_HEIGHT,
						backgroundColor: bgColor,
						boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
						borderRadius: 8,
						overflow: "hidden",
						transform: `scale(${scale})`,
						transformOrigin: "center center",
						userSelect: "none",
					}}
				>
					{bgImgResolved && (
						<div
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								backgroundImage: `url(${bgImgResolved})`,
								backgroundSize: "cover",
								backgroundPosition: "center",
								opacity: bgOpacity,
								pointerEvents: "none",
							}}
						/>
					)}
					{elements.map((el) => (
						<EscapeRoomElement
							key={el.elementId}
							element={el}
							quizData={el.type === "quiz" ? quizzes[el.content?.quizId] : undefined}
							answer={answers[el.elementId]}
							registrationDesign={currentPage?.registrationDesign}
							onButtonAction={handleButtonAction}
							onQuizOptionSelect={handleQuizOptionSelect}
							onHintUsed={handleHintUsed}
						/>
					))}
				</div>
			</div>
			<div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 10, padding: "8px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(0,0,0,0.4)", color: "#fff", pointerEvents: "auto" }}>
				<span>{escapeRoom.name} — Página {pageIndex + 1} / {playablePages.length}</span>
				<span>Puntos: {group?.score ?? 0}</span>
			</div>
			{loading && (
				<div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9998 }}>
					<div style={{ color: "#fff", fontSize: 18 }}>Guardando...</div>
				</div>
			)}
		</div>
	);
}
